import React, { useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { companyInfo, offices } from '../../data/mockData';
import { useLanguage } from '../../context/LanguageContext';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Mail, Phone, MapPin, Send, CheckCircle, Building2, Clock } from 'lucide-react';

export default function ContactSection() {
  const { t, tl } = useLanguage();
  const [sectionRef, isVisible] = useScrollReveal({ threshold: 0.1 });
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = t('contact_err_name');
    if (!formData.email.trim()) errs.email = t('contact_err_email');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errs.email = t('contact_err_email_invalid');
    if (!formData.message.trim()) errs.message = t('contact_err_message');
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
    }, 4000);
  };

  const inputCls = "bg-[#111114] border-[#1e1e24] text-[#f0ebe3] placeholder:text-[#4a4540] focus:border-[#c9a96e]/50 focus:ring-[#c9a96e]/20 h-11 sm:h-12 text-sm";

  return (
    <section id="contact" className="py-16 sm:py-24 lg:py-32 bg-[#0a0a0c]">
      <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 animate-reveal ${isVisible ? 'visible' : ''}`}>
        <div className="flex items-center gap-4 mb-6">
          <span className="text-[#c9a96e] text-xs font-semibold tracking-[0.25em] uppercase">{t('contact_label')}</span>
          <div className="h-px flex-1 bg-[#2a2a30]" />
        </div>

        <div className="max-w-2xl mb-10 sm:mb-16">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#f0ebe3] leading-tight">
            {t('contact_h2_1')}
            <span className="text-gold-gradient block mt-1">{t('contact_h2_2')}</span>
          </h2>
          <p className="text-[#8a857e] text-sm sm:text-base leading-relaxed mt-4 sm:mt-6">{t('contact_sub')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 sm:gap-12 lg:gap-16">
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#c9a96e]/10 flex items-center justify-center mb-5 sm:mb-6">
                  <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-[#c9a96e]" />
                </div>
                <h3 className="text-[#f0ebe3] font-heading text-xl sm:text-2xl font-semibold mb-2">{t('contact_success_title')}</h3>
                <p className="text-[#8a857e] text-sm">{t('contact_success_sub')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#8a857e] text-xs font-medium tracking-wider uppercase">{t('contact_name')} *</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder={t('contact_ph_name')} className={inputCls} />
                    {errors.name && <p className="text-[#8b3030] text-xs">{errors.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#8a857e] text-xs font-medium tracking-wider uppercase">{t('contact_email')} *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder={t('contact_ph_email')} className={inputCls} />
                    {errors.email && <p className="text-[#8b3030] text-xs">{errors.email}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-[#8a857e] text-xs font-medium tracking-wider uppercase">{t('contact_company')}</Label>
                    <Input id="company" name="company" value={formData.company} onChange={handleChange} placeholder={t('contact_ph_company')} className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#8a857e] text-xs font-medium tracking-wider uppercase">{t('contact_phone')}</Label>
                    <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('contact_ph_phone')} className={inputCls} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-[#8a857e] text-xs font-medium tracking-wider uppercase">{t('contact_subject')}</Label>
                  <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} placeholder={t('contact_ph_subject')} className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#8a857e] text-xs font-medium tracking-wider uppercase">{t('contact_message')} *</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder={t('contact_ph_message')} rows={5} className="bg-[#111114] border-[#1e1e24] text-[#f0ebe3] placeholder:text-[#4a4540] focus:border-[#c9a96e]/50 focus:ring-[#c9a96e]/20 resize-none text-sm" />
                  {errors.message && <p className="text-[#8b3030] text-xs">{errors.message}</p>}
                </div>
                <Button type="submit" className="bg-[#c9a96e] text-[#0a0a0c] hover:bg-[#d4bc8a] font-medium text-sm px-8 sm:px-10 py-5 sm:py-6 tracking-wide w-full sm:w-auto justify-center">
                  <Send className="w-4 h-4 mr-2" />
                  {t('contact_submit')}
                </Button>
              </form>
            )}
          </div>

          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <div className="space-y-4 sm:space-y-5">
              <a href={`mailto:${companyInfo.email}`} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 border border-[#1e1e24] rounded-xl bg-[#111114]/60 service-card">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-[#c9a96e]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-[#c9a96e]" />
                </div>
                <div>
                  <p className="text-[#6a6560] text-xs font-medium tracking-wider uppercase mb-1">{t('contact_email_label')}</p>
                  <p className="text-[#f0ebe3] text-sm font-medium">{companyInfo.email}</p>
                </div>
              </a>
              <a href={`tel:${companyInfo.phone}`} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 border border-[#1e1e24] rounded-xl bg-[#111114]/60 service-card">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-[#c9a96e]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-[#c9a96e]" />
                </div>
                <div>
                  <p className="text-[#6a6560] text-xs font-medium tracking-wider uppercase mb-1">{t('contact_phone_label')}</p>
                  <p className="text-[#f0ebe3] text-sm font-medium">{companyInfo.phone}</p>
                </div>
              </a>
              <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 border border-[#1e1e24] rounded-xl bg-[#111114]/60">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-[#c9a96e]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#c9a96e]" />
                </div>
                <div>
                  <p className="text-[#6a6560] text-xs font-medium tracking-wider uppercase mb-1">{t('contact_hq_label')}</p>
                  <p className="text-[#f0ebe3] text-sm font-medium leading-relaxed">{companyInfo.address}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-[#c9a96e] text-xs font-semibold tracking-[0.25em] uppercase mb-4 sm:mb-5">{t('contact_offices_label')}</h4>
              <div className="space-y-2 sm:space-y-3">
                {offices.map((office, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5 sm:py-3 border-b border-[#1a1a1f] last:border-0">
                    <Building2 className="w-4 h-4 text-[#c9a96e]/50 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-[#f0ebe3] text-sm">{office.city}</span>
                      <span className="text-[#6a6560] text-xs ml-2">{'\u2014'} {tl(office.type)}</span>
                    </div>
                    {office.note && (
                      <span className="text-[#c9a96e]/60 text-[10px] font-medium tracking-wider uppercase flex items-center gap-1 flex-shrink-0">
                        <Clock className="w-3 h-3" />{tl(office.note)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 sm:p-5 border border-[#c9a96e]/10 rounded-xl bg-[#c9a96e]/[0.03]">
              <p className="text-[#c9a96e]/70 text-xs font-medium tracking-wider uppercase mb-2">{t('contact_reg_label')}</p>
              <p className="text-[#8a857e] text-xs leading-relaxed">
                {t('contact_reg_text')}{' '}
                <span className="text-[#f0ebe3] font-medium">{companyInfo.registrationNumber}</span>
                {t('contact_reg_date')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
